import { useTours } from 'features/Responder/features/Tours';
import { useZones } from 'features/Responder/features/Zones';
import React, { useMemo } from 'react';
import { Badge, Button, Card, Form, Stack } from 'react-bootstrap';
import { useResponderMap } from 'features/Responder/features/ResponderMap/contexts/ResponderMapContext';

const normalizePoint = (pt) => {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2) {
    const lat = Number(pt[0]);
    const lng = Number(pt[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }
  const lat = Number(pt.lat ?? pt.latitude);
  const lng = Number(pt.lng ?? pt.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const centroid = (points = []) => {
  const pts = points.map(normalizePoint).filter(Boolean);
  if (!pts.length) return null;
  const sum = pts.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), {
    lat: 0,
    lng: 0,
  });
  return { lat: sum.lat / pts.length, lng: sum.lng / pts.length };
};

const layerLabel = (zone) => (Number.isFinite(zone?.layer) ? `Layer ${zone.layer}` : 'Layer ?');

export default function ZoneControlsPanel() {
  const { zones, zonesWithMeta } = useZones();
  const { setCenter } = useTours();
  const {
    selectedZoneIds,
    toggleZoneSelection,
    setSelectedZoneIds,
    isolatedLayerDepth,
    setIsolatedLayerDepth,
    clearIsolation,
  } = useResponderMap();

  const zonesList = useMemo(
    () => (Array.isArray(zonesWithMeta) && zonesWithMeta.length ? zonesWithMeta : zones),
    [zones, zonesWithMeta],
  );

  const selectedKeys = useMemo(() => selectedZoneIds.map((id) => `${id}`), [selectedZoneIds]);

  const allSelected = useMemo(() => {
    if (!Array.isArray(zonesList) || !zonesList.length) return false;
    return zonesList.every((z) => selectedKeys.includes(`${z.id}`));
  }, [selectedKeys, zonesList]);

  const zoneHasPoints = (zone) =>
    Array.isArray(zone?._points)
      ? zone._points.length >= 3
      : Array.isArray(zone?.polygon)
        ? zone.polygon.map(normalizePoint).filter(Boolean).length >= 3
        : false;

  const zoomToZone = (zone) => {
    const points = Array.isArray(zone?._points)
      ? zone._points
      : Array.isArray(zone?.polygon)
        ? zone.polygon.map(normalizePoint).filter(Boolean)
        : [];
    const center = centroid(points);
    if (!center) return;
    setCenter([center.lat, center.lng, 16]);
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <Card.Title className="mb-0">Zones on Map</Card.Title>
            <Card.Text className="text-muted small mb-0">
              Toggle zone visibility and isolate by layer.
            </Card.Text>
          </div>
          {isolatedLayerDepth != null ? (
            <Button size="sm" variant="outline-light" onClick={clearIsolation}>
              Show all layers
            </Button>
          ) : (
            <Badge bg="secondary">All layers</Badge>
          )}
        </div>
        <div className="d-flex justify-content-end mb-2">
          <Button
            size="sm"
            variant="outline-primary"
            disabled={!zones?.length}
            onClick={() => {
              if (allSelected) {
                setSelectedZoneIds([]);
                return;
              }
              const allIds = zonesList.map((z) => z.id).filter((id) => id != null);
              setSelectedZoneIds(allIds);
            }}
          >
            {allSelected ? 'Hide all zones' : 'Show all zones'}
          </Button>
        </div>
        {Array.isArray(zonesList) && zonesList.length ? (
          <Stack gap={2}>
            {zonesList.map((zone) => {
              const isChecked = selectedKeys.includes(`${zone.id}`);
              const layer = Number.isFinite(zone.layer) ? zone.layer : null;
              const isIsolated = isolatedLayerDepth != null && layer === isolatedLayerDepth;
              return (
                <div
                  key={zone.id || zone.name}
                  className={`d-flex justify-content-between align-items-start p-2 rounded ${isChecked ? 'bg-warning bg-opacity-10 border border-warning' : 'border border-secondary-subtle'}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleZoneSelection(zone.id)}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Check
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleZoneSelection(zone.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div>
                      <div className="fw-semibold">{zone.name || 'Zone'}</div>
                      <div className="small text-muted d-flex gap-2 align-items-center flex-wrap">
                        <span>{layerLabel(zone)}</span>
                        <span>•</span>
                        <span>
                          {zone.includeInParent === false
                            ? 'Excluded from parent totals'
                            : 'Included in parent totals'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-1 align-items-end">
                    {layer != null && (
                      <Badge bg={isIsolated ? 'warning' : 'secondary'}>
                        {isIsolated ? 'Isolated' : `Layer ${layer}`}
                      </Badge>
                    )}
                    <div className="d-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={!zoneHasPoints(zone)}
                        onClick={(e) => {
                          e.stopPropagation();
                          zoomToZone(zone);
                        }}
                      >
                        Zoom
                      </Button>
                      <Button
                        size="sm"
                        variant={isIsolated ? 'warning' : 'outline-warning'}
                        disabled={layer == null}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (layer == null) return;
                          setIsolatedLayerDepth((prev) => (prev === layer ? null : layer));
                        }}
                      >
                        {isIsolated ? 'Show all' : 'Isolate'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </Stack>
        ) : (
          <div className="text-muted small">No zones available.</div>
        )}
      </Card.Body>
    </Card>
  );
}
